import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";

import { useDialog } from "@datanac/datanac-mui-toolkit";
import { Grid, Typography } from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid";
import { UsersApiHelperForMember } from "api/ApiHelper";
import { AppContext } from "App";

import { formatDate, formatPrice } from "../../../utility/formatters";
import { DeleteIcon, NavigateIcon } from "../../Icons/Icons";
import { PrintableTilleyDataGrid } from "../../shared/DataGrid/PrintableTilleyDataGrid";

export const DebtScheduleAmortizationTable = ({
  debt_schedule_id,
  equipment_id = null,
  calculatedSchedule = null,
  onScheduleChange = null,
  onDataLoaded = null,
}) => {
  const { globalState } = useContext(AppContext);
  const [isLoading, setIsLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const navigate = useNavigate();
  const { component: confirmDialogComponent, actions: confirmDialogActions } = useDialog();

  const api = UsersApiHelperForMember;

  const loadData = useCallback(async () => {
    if (!debt_schedule_id || String(debt_schedule_id) === "0") return;

    try {
      setIsLoading(true);
      const payload = {
        producer_token: globalState.producer_token,
        debt_schedule_id: debt_schedule_id,
        is_active: true,
      };

      const response = await api.callPost("DebtScheduleAmortization/search", payload, globalState);
      if (response) {
        setRows(response);
        // Notify parent component that schedule data has changed
        if (onScheduleChange) {
          onScheduleChange();
        }
        // Pass the loaded data to parent
        if (onDataLoaded) {
          onDataLoaded(response);
        }
      }
    } catch (error) {
      console.error("Error loading amortization schedule", error);
    } finally {
      setIsLoading(false);
    }
  }, [debt_schedule_id, globalState?.producer_token, onScheduleChange]);

  // Use calculatedSchedule if provided
  useEffect(() => {
    if (calculatedSchedule && calculatedSchedule.length > 0) {
      setRows(calculatedSchedule);
      setIsLoading(false);
      // Pass the calculated schedule to parent
      if (onDataLoaded) {
        onDataLoaded(calculatedSchedule);
      }
    }
  }, [calculatedSchedule, onDataLoaded]);

  // Load from API only when no calculated schedule is provided
  useEffect(() => {
    if (!calculatedSchedule || calculatedSchedule.length === 0) {
      loadData();
    }
  }, [debt_schedule_id, globalState?.producer_token]);

  const handleDelete = useCallback(
    (itemToDelete) => {
      confirmDialogActions.confirm({
        title: "Delete Amortization Entry?",
        prompt: `Are you sure you want to delete payment #${itemToDelete.payment_number}?`,
        promptDetail: "This action cannot be undone.",
        callback: async () => {
          try {
            await api.callPost(
              "DebtScheduleAmortization",
              {
                DebtScheduleAmortization: [{ ...itemToDelete, is_active: false }],
                producer_token: globalState.producer_token,
              },
              globalState
            );
            await loadData();
            // Notify parent component that schedule data has changed
            if (onScheduleChange) {
              onScheduleChange();
            }
          } catch (error) {
            console.error("Failed to delete amortization entry", error);
          }
        },
      });
    },
    [api, confirmDialogActions, globalState, loadData, onScheduleChange]
  );

  const columns = useMemo(
    () => [
      {
        field: "payment_number",
        headerName: "#",
        width: 80,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "payment_date",
        headerName: "Due Date",
        width: 120,
        valueFormatter: (value) => (value ? formatDate(value) : ""),
      },
      {
        field: "beginning_balance",
        headerName: "Balance",
        flex: 1,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value) => (value ? formatPrice(value, 0) : ""),
        cellClassName: "grid-column-group-a first",
      },
      {
        field: "payment_amount",
        headerName: "Payment",
        flex: 1,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value) => (value ? formatPrice(value, 0) : ""),
        cellClassName: "grid-column-group-a bold-header",
        headerClassName: "bold-header",
      },
      {
        field: "principal_payment",
        headerName: "Principal",
        flex: 1,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value) => (value ? formatPrice(value, 0) : ""),
        cellClassName: "grid-column-group-a",
      },
      {
        field: "interest_payment",
        headerName: "Interest",
        flex: 1,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value) => (value ? formatPrice(value, 0) : ""),
        cellClassName: "grid-column-group-a",
      },
      {
        field: "extra_principal_payment",
        headerName: "Extra Principal",
        flex: 1,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value) => (value ? formatPrice(value, 0) : formatPrice(0, 0)),
        cellClassName: "grid-column-group-a",
      },
      {
        field: "cumulative_principal",
        headerName: "Cumulative Principal",
        flex: 1,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value) => (value ? formatPrice(value, 0) : ""),
        cellClassName: "grid-column-group-b first",
      },
      {
        field: "cumulative_interest",
        headerName: "Cumulative Interest",
        flex: 1,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value) => (value ? formatPrice(value, 0) : ""),
        cellClassName: "grid-column-group-b",
      },
      {
        field: "notes",
        headerName: "Notes",
        width: 100,
        align: "left",
        headerAlign: "left",
        renderCell: (params) => {
          const notes = params.value || "";
          return notes.substring(0, 10);
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        type: "actions",
        headerAlign: "right",
        align: "right",
        width: 100,
        sortable: false,
        getActions: (params) => [
          <GridActionsCellItem
            data-testid={`delete-icon-${params?.row?.payment_number}`}
            key="delete"
            icon={<DeleteIcon />}
            label="Delete"
            onClick={() => handleDelete(params.row)}
          />,
          <GridActionsCellItem
            key="edit"
            data-testid={`edit-icon-${params?.row?.payment_number}`}
            icon={<NavigateIcon />}
            label="Edit"
            onClick={() => {
              if (equipment_id) {
                navigate(`/financial/equipment/${equipment_id}/${debt_schedule_id}/${params?.row?.id}`);
              } else {
                navigate(`/financial/debt_schedule/${debt_schedule_id}/${params?.row?.id}`);
              }
            }}
          />,
        ],
      },
    ],
    [navigate, debt_schedule_id, handleDelete, equipment_id]
  );
  if (!debt_schedule_id || String(debt_schedule_id) === "0") return null;

  return (
    <>
      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Amortization
          </Typography>
          <PrintableTilleyDataGrid
            hideColumns={["actions"]}
            rows={rows}
            columns={columns}
            getRowId={(row) => row.id}
            isLoading={isLoading}
            gridKey="debt-schedule-amortization"
            defaultPageSize={100}
            pageSizeOptions={[10, 25, 50, 100]}
            sx={{ width: "100%", boxSizing: "border-box" }}
            autoHeight
          />
        </Grid>
      </Grid>
      {confirmDialogComponent}
    </>
  );
};
